import {
  CompassCardComponent,
  CompassCardComponentProps,
} from 'react-scale/corp/views/DataCompassV2/types';
import { useCompassCard } from '@scale/compass-ui/store';
import {
  type TransformScriptContext,
  CompassTransformScript,
  CompassTransformScriptMetadata,
  CompassScriptLabelType,
} from '@scale/genai-compass-shared/cards/transform_script';
import { useState, useMemo, useEffect } from 'react';
import { useDebounce } from 'react-use-v13';
import { FormField, TextField, Select, Separator } from '@scale/scaleui-radix';
import TransformScriptDataTable from './components/TransformScriptDataTable';
import FdeScriptTransformerHeader from './components/FdeScriptTransformerHeader';
import SelectedScriptCallout from './components/SelectedScriptCallout';
import { getDefaultScriptParams } from './models/ScriptParams';

const TransformScriptComponent: CompassCardComponent = ({ index }: CompassCardComponentProps) => {
  const { card, updateCardContext, previousCardData } = useCompassCard(index);
  const context = card.defaultContext as TransformScriptContext;

  // Initialize local script from context
  const [localScript, setLocalScript] = [
    context.script,
    (script: CompassTransformScript | undefined) => {
      // When script changes, reset params to defaults for that script
      const scriptParams = script ? getDefaultScriptParams(script) : {};
      updateCardContext({ ...context, script, scriptParams } as TransformScriptContext);
    },
  ];

  // Initialize local params from context or defaults
  const [localScriptParams, setLocalScriptParams] = useState(
    context.scriptParams || (context.script ? getDefaultScriptParams(context.script) : {}),
  );

  const [selectedCustomer, setSelectedCustomer] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Ensure script and parameters are properly initialized
  useEffect(() => {
    if (localScript) {
      const defaultParams = getDefaultScriptParams(localScript);

      // Ensure we have the default parameters for the current script
      if (!localScriptParams || Object.keys(localScriptParams).length === 0) {
        setLocalScriptParams(defaultParams);
      }

      // Ensure the context has the script and parameters
      if (
        context.script !== localScript ||
        !context.scriptParams ||
        Object.keys(context.scriptParams).length === 0
      ) {
        updateCardContext({
          ...context,
          script: localScript,
          scriptParams: defaultParams,
        } as TransformScriptContext);
      }
    }
  }, [localScript, context.script]);

  // Handler for script selection changes
  const handleScriptChange = (script: CompassTransformScript | undefined) => {
    if (script) {
      const scriptParams = getDefaultScriptParams(script);

      // Update local state
      setLocalScript(script);
      setLocalScriptParams(scriptParams);

      // Update context immediately
      updateCardContext({
        ...context,
        script,
        scriptParams,
      } as TransformScriptContext);
    } else {
      setLocalScript(undefined);
      setLocalScriptParams({});
    }
  };

  // Sync local params to context when they change
  useDebounce(
    () => {
      if (
        localScript &&
        JSON.stringify(localScriptParams) !== JSON.stringify(context.scriptParams)
      ) {
        updateCardContext({
          ...context,
          script: localScript, // Ensure script is also included
          scriptParams: localScriptParams,
        } as TransformScriptContext);
      }
    },
    200,
    [localScriptParams, context.scriptParams, localScript],
  );

  // Get unique customers from metadata
  const customers = useMemo(() => {
    const customerSet = new Set<string>();
    Object.values(CompassTransformScriptMetadata).forEach(metadata => {
      metadata.labels.forEach(label => {
        if (label.type === CompassScriptLabelType.CUSTOMER_NAME) {
          customerSet.add(label.value);
        }
      });
    });
    return Array.from(customerSet);
  }, []);

  const handleScriptParamsChange = (params: Record<string, any>) => {
    setLocalScriptParams(params);
  };

  return (
    <div className="flex flex-col gap-4">
      <FdeScriptTransformerHeader />

      <Separator className="mt-2 mb-4 w-full" />

      <div className="flex flex-col gap-2 mb-3">
        <div className="flex gap-4 mb-4">
          <FormField.Root size="3" className="w-48">
            <FormField.Label>Filter by Customer</FormField.Label>
            <Select.Root value={selectedCustomer} onValueChange={setSelectedCustomer}>
              <Select.Trigger data-testid="customer-select" />
              <Select.Content>
                <Select.Item value="all">All Customers</Select.Item>
                {customers.map(customer => (
                  <Select.Item key={customer} value={customer}>
                    {customer}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </FormField.Root>

          <FormField.Root size="3" className="flex-1">
            <FormField.Label>Search</FormField.Label>
            <TextField.Root
              data-testid="search-input"
              placeholder="Search by name, description, or label..."
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
            />
          </FormField.Root>
        </div>

        <TransformScriptDataTable
          selectedCustomer={selectedCustomer}
          searchQuery={searchQuery}
          localScript={localScript}
          onScriptChange={handleScriptChange}
        />

        <SelectedScriptCallout
          localScript={localScript}
          scriptParams={localScriptParams}
          onScriptParamsChange={handleScriptParamsChange}
          columns={previousCardData?.columns}
        />
      </div>
    </div>
  );
};

export default TransformScriptComponent;
